/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState, useEffect } from 'react'
import { type Table } from '@tanstack/react-table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { UserCheck, UserMinus } from 'lucide-react'
import { DataTableBulkActions as BulkActionsToolbar } from '@/components/data-table'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuthStore } from '@/stores/auth-store'
import { ROLE } from '@/lib/roles'
import { assignUsers, unassignUsers, getAdmins } from '../api'
import { useUsers } from './users-provider'
import { type User } from '../types'

interface DataTableBulkActionsProps {
  table: Table<User>
}

export function DataTableBulkActions({ table }: DataTableBulkActionsProps) {
  const { t } = useTranslation()
  const userRole = useAuthStore((state) => state.auth.user?.role)
  const isSuperAdmin = userRole === ROLE.SUPER_ADMIN
  const [selectedAdminId, setSelectedAdminId] = useState<string>('')
  const { triggerRefresh } = useUsers()

  const { data: adminsData } = useQuery({
    queryKey: ['admins'],
    queryFn: getAdmins,
    enabled: isSuperAdmin,
  })

  const admins = adminsData?.data || []

  const selectedRows = table.getFilteredSelectedRowModel().rows
  const selectedUserIds = selectedRows.map((row) => row.original.id)
  const hasAdminSelected = selectedRows.some(
    (row) => row.original.role >= ROLE.ADMIN
  )

  useEffect(() => {
    setSelectedAdminId('')
  }, [selectedRows.length])

  if (!isSuperAdmin) {
    return (
      <BulkActionsToolbar table={table} entityName='用户'>
        <></>
      </BulkActionsToolbar>
    )
  }

  const handleAssign = async () => {
    if (hasAdminSelected) {
      toast.error(t('只能分配普通用户'))
      return
    }
    const adminId = Number(selectedAdminId)
    if (!adminId) {
      toast.error(t('Please select an admin'))
      return
    }
    const res = await assignUsers({ user_ids: selectedUserIds, admin_id: adminId })
    if (res.success) {
      toast.success(t('Users assigned successfully'))
      table.resetRowSelection()
      triggerRefresh()
    } else {
      toast.error(res.message || t('Failed to assign users'))
    }
  }

  const handleUnassign = async () => {
    const res = await unassignUsers({ user_ids: selectedUserIds })
    if (res.success) {
      toast.success(t('Users unassigned successfully'))
      table.resetRowSelection()
      triggerRefresh()
    } else {
      toast.error(res.message || t('Failed to unassign users'))
    }
  }

  return (
    <BulkActionsToolbar table={table} entityName='用户'>
      <Select value={selectedAdminId} onValueChange={(value) => setSelectedAdminId(value || '')}>
        <SelectTrigger className='h-8 w-[140px] text-xs'>
          <SelectValue placeholder={t('Select admin')} />
        </SelectTrigger>
        <SelectContent>
          {admins.map((admin: User) => (
            <SelectItem key={admin.id} value={String(admin.id)}>
              {admin.display_name || admin.username}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant='outline'
        size='sm'
        onClick={handleAssign}
        className='h-8'
        disabled={!selectedAdminId || hasAdminSelected}
        title={hasAdminSelected ? t('只能分配普通用户') : undefined}
      >
        <UserCheck className='mr-1 h-4 w-4' />
        {t('Assign')}
      </Button>
      <Button
        variant='outline'
        size='sm'
        onClick={handleUnassign}
        className='h-8'
      >
        <UserMinus className='mr-1 h-4 w-4' />
        {t('Unassign')}
      </Button>
    </BulkActionsToolbar>
  )
}